import { HailingStepData, INITIAL_HAILING_STATE } from "./mock/data";
import { HailingQuoteResponse } from "../../api/meHailingQuote.service";
import type { ActiveHailingOrderSnapshot } from "../../api/meHailingOrder.service";

// Pure helpers of the booking flow (HailingFlow), kept apart from the component.

export const quoteIsFresh = (quote: HailingQuoteResponse | undefined): boolean => {
  if (!quote) return false;
  if (!quote.expiresAt) return true;
  const expiresAt = Date.parse(quote.expiresAt);
  return Number.isFinite(expiresAt) && expiresAt > Date.now();
};

export const restoreActiveOrder = (
  order: ActiveHailingOrderSnapshot,
  language: string,
): HailingStepData | null => {
  const itinerary = order.itinerary;
  const pickup = itinerary[0];
  const dropoff = itinerary[itinerary.length - 1];
  const vehicle =
    order.matching?.selectedVehicleClassOptions[0] ??
    order.matching?.lowestVehicleClassOption;
  const fare = order.matching?.minMaxFareCalculations[0];
  const amount = order.userTotal ?? fare?.discountedTotal ?? fare?.total;

  if (
    !order.hailingRequestId ||
    !pickup ||
    !dropoff ||
    !vehicle ||
    amount == null ||
    !Number.isFinite(amount)
  ) {
    return null;
  }

  const placeDetails = (point: typeof pickup) => {
    const localized = language.startsWith("zh")
      ? point.i18n?.zhHK ?? point.i18n?.en
      : point.i18n?.en ?? point.i18n?.zhHK;
    return {
      place: localized?.displayName ?? "",
      address: localized?.formattedAddress ?? "",
    };
  };
  const pickupDetails = placeDetails(pickup);
  const dropoffDetails = placeDetails(dropoff);
  const stop = itinerary.length > 2 ? itinerary[1] : undefined;
  const stopDetails = stop ? placeDetails(stop) : undefined;
  const fareKind =
    order.isFixedPrice || fare?.fixedPrice != null ? "flat" : "meter";
  const money = new Intl.NumberFormat("en-HK", {
    style: "currency",
    currency: "HKD",
    maximumFractionDigits: 2,
  });
  const vehicleName =
    language.startsWith("zh")
      ? vehicle.nameTc || vehicle.name
      : vehicle.name || vehicle.nameTc;
  const cardSuffix = order.payment?.cardSuffix;

  return {
    ...INITIAL_HAILING_STATE,
    currentStep: "ride",
    pickupPlace: pickupDetails.place,
    pickupAddress: pickupDetails.address,
    pickupPlaceId: pickup.placeId ?? "",
    pickupLat: pickup.lat,
    pickupLng: pickup.lng,
    dropoffPlace: dropoffDetails.place,
    dropoffAddress: dropoffDetails.address,
    dropoffPlaceId: dropoff.placeId ?? "",
    dropoffLat: dropoff.lat,
    dropoffLng: dropoff.lng,
    ...(stop && stopDetails
      ? {
          stopPlace: stopDetails.place,
          stopAddress: stopDetails.address,
          stopPlaceId: stop.placeId ?? "",
          stopLat: stop.lat,
          stopLng: stop.lng,
        }
      : {}),
    scheduledTime:
      order.type?.toUpperCase() === "SCHEDULED" ? order.request.time ?? null : null,
    vehicleClass: {
      key: vehicle.key,
      name: vehicleName ?? vehicle.key,
      blurb:
        (language.startsWith("zh")
          ? vehicle.descriptionTc || vehicle.description
          : vehicle.description || vehicle.descriptionTc) ?? "",
      seats: vehicle.numberOfSeat ?? 0,
      bags: vehicle.numberOfLuggage ?? 0,
      eta: 0,
      meter: amount,
      flat: amount,
      price: money.format(amount),
      vehicleClassCode: vehicle.key,
      operatingArea: order.request.operatingAreas,
      isFixedFare: fareKind === "flat",
      ...(vehicle.imageUrl ? { iconUrl: vehicle.imageUrl } : {}),
    },
    fareKind,
    fareEstimate: money.format(amount),
    paymentMethod: {
      id: "",
      name: order.payment?.cardType ?? "Card",
      sub: cardSuffix ? `•••• ${cardSuffix}` : "",
      type: "card",
    },
    hailingRequestId: order.hailingRequestId,
    ...(order.live?.driverLocation
      ? { driverPosition: order.live.driverLocation }
      : {}),
    ...(order.driver?.operatingArea
      ? { driverOperatingArea: order.driver.operatingArea }
      : {}),
    ...(order.tripEstimation?.encodedPolyline
      ? { routePolyline: order.tripEstimation.encodedPolyline }
      : {}),
  };
};

export const TERMINAL_ORDER_STATUSES = ["COMPLETED", "CANCELLED", "TIMED_OUT"];
